import { SaveAlt as SaveAltIcon } from '@mui/icons-material';
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Card, CardContent, Chip, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { apiRequest } from "api-client";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { openPopup } from "utils/window";

interface VideoUploadProps {
  disabled?: boolean;
  videoSrcUrl?: string;
  eventId: number;
  videoTitle: string;
  onVideoDurationChange: (duration: number) => void;
  onUploaded?: (args: { url?: string; bunnyVideoId?: string; }) => void;
}

const VideoUpload = (props: VideoUploadProps) => {
  const [uploadTo, setUploadTo] = useState('bunny');
  const [description, setDescription] = useState('');
  const [uploaded, setUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [file, setFile] = useState<File>();

  /**
   * Prepares the YouTube upload by checking if the user is authenticated.
   * If not authenticated, it opens a popup for authentication.
   * After authentication, collects the title and description for the video.
   * Waits for the modal to close before proceeding.
   */
  const prepareYoutubeUpload = async () => {
    try {
      const authenticatedRes = await apiRequest.get(`/youtube/authenticated`);
      if (!authenticatedRes.authenticated) {
        const authUrlRes = await apiRequest.get(`/youtube/auth-url`);
        await openPopup(authUrlRes.authUrl, 'authPopup', 'width=600,height=600');
      }

    } catch (e: any) {
      console.error(e);
      toast.error(e.message);
    }
  };

  const uploadUrl = useMemo(() => {
    if (uploadTo === 'bunny' && props.videoSrcUrl) {
      return `/bunny/fetch`;
    }
    if (uploadTo === 'bunny' && !props.videoSrcUrl) {
      return `/bunny/upload`;
    }
    return `/youtube/upload`;
  }, [uploadTo, props.videoSrcUrl]);

  const handleUploadToChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setUploadTo(event.target.value);
    if (event.target.value === 'youtube') {
      await prepareYoutubeUpload();
    }
  };

  const handleUpload = () => {
    setUploading(true);
    const form = new FormData();
    if (props.videoSrcUrl) {
      form.append('videoSrcUrl', props.videoSrcUrl);
    }
    form.append('title', props.videoTitle);
    form.append('description', description);
    form.append('collectionName', `Event ${props.eventId}`);

    if (file) {
      form.append('file', file, file.name);
    }

    if (!file && !props.videoSrcUrl) {
      toast.error('There must be a videoSrcUrl or a file selected');
      return;
    }

    apiRequest.post(uploadUrl, form)
      .then((data) => {
        toast.success('Video uploaded successfully');
        if (props.onUploaded) {
          props.onUploaded({
            url: data.url,
            bunnyVideoId: data.bunnyVideoId,
          });
        }
        setUploaded(true);
        setUploading(false);
      })
      .catch(err => {
        toast.error(err.message || 'Failed to upload video');
        setUploading(false);
      });
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0]);
  }

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }
    const videoElement = videoRef.current;
    if (file) {
      videoElement.src = URL.createObjectURL(file);
    }

    // Extract duration once metadata is loaded
    const onLoadedMetadata = () => {
      props?.onVideoDurationChange(Math.floor(videoElement.duration));
      videoElement.removeEventListener('loadedmetadata', onLoadedMetadata);
    };

    videoElement.addEventListener('loadedmetadata', onLoadedMetadata);
  }, [videoRef.current, file]);

  return (
    <Card variant="outlined">
      <Box position="relative">
        {(props.videoSrcUrl || file) &&
          <>
            <video
              ref={videoRef}
              controls
              width="100%"
              height="auto"
              style={{ maxHeight: '500px' }}
            >
              <source src={props.videoSrcUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <Chip
              label="Preview"
              color="info"
              size="small"
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                zIndex: 1,
                opacity: 0.8,
              }}
            />
          </>
        }

      </Box>
      <CardContent>
        {props.videoSrcUrl &&
          <Box textAlign="right">
            <a href={props.videoSrcUrl} download target="_blank" rel="noopener noreferrer">
              <SaveAltIcon />
            </a>
          </Box>
        }
        {!props.videoSrcUrl &&
          <Box mb={2}>
            <input ref={inputRef} type="file" accept="video/*" onChange={onFileChange} disabled={props.disabled} />
          </Box>
        }
        <Box>
          <FormControl>
            <FormLabel id="upload-to-buttons-group-label">Upload To</FormLabel>
            <RadioGroup
              aria-labelledby="upload-to-buttons-group-label"
              value={uploadTo}
              onChange={handleUploadToChange}
              name="radio-buttons-group"
              row
            >
              <FormControlLabel value="bunny" control={<Radio />} label="Bunny" />
              <FormControlLabel value="youtube" control={<Radio />} label="Youtube" />
            </RadioGroup>
          </FormControl>
        </Box>
        {uploaded &&
          <Box mt={2}>
            <Alert severity="success" variant="outlined">
              Video uploaded successfully! Please continue to add other details and publish the video.
            </Alert>
          </Box>
        }

        {!uploaded &&
          <Box mt={1}>
            <Typography variant="h6">
              Add Video Details
            </Typography>
            {props.videoTitle}
            {uploadTo === 'youtube' &&
              <TextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                margin="normal"
                multiline
                rows={4}
              />
            }
          </Box>
        }
        {!uploaded &&
          <LoadingButton
            variant="outlined"
            color="primary"
            size="small"
            sx={{ mt: 1 }}
            onClick={handleUpload}
            disabled={props.disabled}
            loading={uploading}
          >
            Upload
          </LoadingButton>
        }
      </CardContent>
    </Card>
  );
}

export default VideoUpload;